import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Menu, X, Music, Disc3, Calendar, Phone, Star, Image, Play, Download, ChevronRight, Quote, MapPin, Clock, ExternalLink, Mail, Check, Shield, Bell, Trash2, CheckCircle, XCircle, Plus, Edit, Search } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== NAVIGATION ==============
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/gallery", label: "Gallery" },
    { path: "/videos", label: "Videos" },
    { path: "/shop", label: "CD Shop" },
    { path: "/songlist", label: "Songlist" },
    { path: "/gig-guide", label: "Gig Guide" },
    { path: "/reviews", label: "Reviews" },
    { path: "/contact", label: "Contact" },
    { path: "/promotion", label: "Promotion" },
  ];

  return (
    <nav data-testid="navigation" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-black/5">
      <div className="container-padding">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="nav-logo" className="font-heading text-xl md:text-2xl font-black tracking-tighter text-text-primary">
            DAVE STEWART
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-medium transition-colors hover:text-accent ${location.pathname === link.path ? "text-accent" : "text-text-secondary"}`}>
                {link.label}
              </Link>
            ))}
          </div>
          <button data-testid="mobile-menu-button" onClick={() => setIsOpen(!isOpen)} className="lg:hidden p-2">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {isOpen && (
          <div data-testid="mobile-menu" className="lg:hidden py-4 border-t border-border">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} onClick={() => setIsOpen(false)}
                className={`block py-3 text-base font-medium transition-colors hover:text-accent ${location.pathname === link.path ? "text-accent" : "text-text-secondary"}`}>
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

// ============== HOME PAGE ==============
const Home = () => {
  return (
    <div data-testid="home-page">
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src="https://img.youtube.com/vi/1iDTYnxCNXM/maxresdefault.jpg" alt="Dave Stewart performing Zombie on Drottninggatan" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/55"></div>
        </div>
        <div className="relative container-padding section-spacing">
          <div className="max-w-3xl">
            <p className="overline mb-4">MUSICIAN / ENTERTAINER / SWEDEN</p>
            <h1 className="heading-1 mb-6">DAVE STEWART</h1>
            <p className="body-text text-lg mb-3 max-w-xl">
              Scotland's seasoned performer based in Sweden. Over 34 years dazzling audiences across Europe with unforgettable rock and pop covers and engaging sing-alongs.
            </p>
            <p className="body-text text-base mb-8 max-w-xl">
              30K+ YouTube subscribers. 20 million+ views. From Switzerland to Scotland, England to Estonia, Scandinavia to the Baltics. Available for pubs, weddings, corporate events, and festivals.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-primary">Browse Albums</Link>
              <Link to="/contact" data-testid="hero-contact-btn" className="btn-secondary">Book Dave</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Video */}
      <section className="section-spacing bg-bg-secondary">
        <div className="container-padding">
          <p className="overline mb-4">VIRAL HIT</p>
          <h2 className="heading-2 mb-8">20 Million+ Views</h2>
          <div className="max-w-4xl">
            <div className="aspect-video border border-border overflow-hidden">
              <iframe
                data-testid="featured-video"
                src="https://www.youtube.com/embed/1iDTYnxCNXM"
                title="Dave Stewart - Zombie (Cranberries Cover)"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-spacing">
        <div className="container-padding">
          <h2 className="heading-2 mb-12 text-center">Explore</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={<Disc3 className="w-8 h-8" />} title="CD Shop" description="19 albums including studio, live, DVD, and custom CDs." link="/shop" testId="feature-shop" />
            <FeatureCard icon={<Calendar className="w-8 h-8" />} title="Gig Guide" description="Check upcoming performances and book Dave for your event." link="/gig-guide" testId="feature-gigs" />
            <FeatureCard icon={<Music className="w-8 h-8" />} title="Songlist" description="75+ artists. Hundreds of songs. Classic rock, folk, pop, and more." link="/songlist" testId="feature-songlist" />
            <FeatureCard icon={<Star className="w-8 h-8" />} title="Reviews" description="Read what venues and audiences say about Dave's performances." link="/reviews" testId="feature-reviews" />
            <FeatureCard icon={<Image className="w-8 h-8" />} title="Gallery" description="Photos from live performances on Drottninggatan and events." link="/gallery" testId="feature-gallery" />
            <FeatureCard icon={<Play className="w-8 h-8" />} title="Videos" description="Watch live performance videos from YouTube." link="/videos" testId="feature-videos" />
          </div>
        </div>
      </section>

      {/* Social Links */}
      <section className="section-spacing bg-bg-secondary">
        <div className="container-padding text-center">
          <h2 className="heading-2 mb-8">Connect</h2>
          <div className="flex justify-center gap-6">
            <a href="https://www.youtube.com/@dave.r.stewart" target="_blank" rel="noopener noreferrer" data-testid="social-youtube" className="p-4 border border-border hover:border-accent hover:text-accent transition-colors">
              <Play size={32} />
            </a>
            <a href="http://twitter.com/justdave63" target="_blank" rel="noopener noreferrer" data-testid="social-twitter" className="p-4 border border-border hover:border-accent hover:text-accent transition-colors">
              <ExternalLink size={32} />
            </a>
            <a href="http://www.facebook.com/dave.r.stewart" target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="p-4 border border-border hover:border-accent hover:text-accent transition-colors">
              <ExternalLink size={32} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, link, testId }) => (
  <Link to={link} data-testid={testId} className="group p-8 bg-white border border-border hover:border-accent transition-all duration-300">
    <div className="text-accent mb-4">{icon}</div>
    <h3 className="heading-3 mb-2 group-hover:text-accent transition-colors">{title}</h3>
    <p className="body-text">{description}</p>
    <div className="mt-4 flex items-center text-accent font-medium">
      View <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
    </div>
  </Link>
);

// ============== GALLERY (Real YouTube thumbnails) ==============
const Gallery = () => {
  const images = [
    { url: "https://img.youtube.com/vi/1iDTYnxCNXM/maxresdefault.jpg", alt: "Dave Stewart - Zombie cover on Drottninggatan" },
    { url: "https://img.youtube.com/vi/zunNvt6jpsc/maxresdefault.jpg", alt: "Dave Stewart - Live at the Cinema" },
    { url: "https://img.youtube.com/vi/Ek_TohB4O5w/maxresdefault.jpg", alt: "Dave Stewart - Live in OIP Denmark" },
    { url: "https://img.youtube.com/vi/AsQIKl3SdXY/maxresdefault.jpg", alt: "Dave Stewart - Don't Look Back In Anger" },
    { url: "https://img.youtube.com/vi/8n4f15TYhj8/maxresdefault.jpg", alt: "Dave Stewart - Fields of Gold" },
    { url: "https://img.youtube.com/vi/AoSAlyuQPE8/maxresdefault.jpg", alt: "Dave Stewart - Don't Stop Believin'" },
    { url: "https://img.youtube.com/vi/mVuuExooOt8/maxresdefault.jpg", alt: "Dave Stewart - Free Fallin'" },
    { url: "https://img.youtube.com/vi/ESiTX-mSSV0/maxresdefault.jpg", alt: "Dave Stewart - Live Stockholm City" },
    { url: "https://img.youtube.com/vi/MRA6SLZzxeY/maxresdefault.jpg", alt: "Dave Stewart - I'm Yours" },
    { url: "https://img.youtube.com/vi/zu7USQiUjlk/maxresdefault.jpg", alt: "Dave Stewart - Redemption Song at Antique" },
    { url: "https://img.youtube.com/vi/6geXKg4n4tw/maxresdefault.jpg", alt: "Dave Stewart Live Performance" },
    { url: "https://www.dave63.com/logo1.jpg", alt: "Dave Stewart Logo" },
    { url: "https://www.dave63.com/poster.jpg", alt: "Dave Stewart Concert Poster" },
    { url: "https://www.dave63.com/tartan.jpg", alt: "Dave Stewart Tartan Poster" },
  ];

  return (
    <div data-testid="gallery-page" className="pt-20">
      <div className="container-padding section-spacing">
        <h1 className="heading-1 mb-4">Gallery</h1>
        <p className="body-text mb-12 max-w-2xl">Photos from live performances on Drottninggatan, Stockholm and events.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((image, index) => (
            <div key={index} data-testid={`gallery-image-${index}`} className="group relative aspect-video overflow-hidden border border-border">
              <img src={image.url} alt={image.alt} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-end">
                <p className="text-white text-sm p-3 opacity-0 group-hover:opacity-100 transition-opacity">{image.alt}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== VIDEOS PAGE (Real YouTube embeds) ==============
const Videos = () => {
  const videos = [
    { id: "1iDTYnxCNXM", title: "Zombie - Cranberries Cover (20M+ views)", description: "Live on Drottninggatan, Stockholm" },
    { id: "zunNvt6jpsc", title: "LIVE AT THE CINEMA", description: "Full live set - subscribe and like!" },
    { id: "Ek_TohB4O5w", title: "Live in OIP Denmark", description: "Live performance in Denmark" },
    { id: "AsQIKl3SdXY", title: "Don't Look Back In Anger - Oasis Cover", description: "Live on Drottninggatan, Stockholm" },
    { id: "8n4f15TYhj8", title: "Fields of Gold - Sting Cover", description: "Live on Drottninggatan, Stockholm" },
    { id: "AoSAlyuQPE8", title: "Don't Stop Believin' - Journey Cover", description: "Stockholm Street Performance" },
    { id: "mVuuExooOt8", title: "Free Fallin' - Tom Petty Cover", description: "Drottninggatan, Stockholm (2011)" },
    { id: "ESiTX-mSSV0", title: "Live @ Stockholm City", description: "Awesome street singing session" },
    { id: "MRA6SLZzxeY", title: "I'm Yours - Jason Mraz Cover", description: "SM for Gatumusikanter, Mariefred" },
    { id: "zu7USQiUjlk", title: "Redemption Song - Bob Marley Cover", description: "Live at Antique, Stockholm (2014)" },
    { id: "6geXKg4n4tw", title: "Live Performance", description: "Street performance" },
  ];

  return (
    <div data-testid="videos-page" className="pt-20">
      <div className="container-padding section-spacing">
        <h1 className="heading-1 mb-4">Videos</h1>
        <p className="body-text mb-4 max-w-2xl">Watch Dave's live performances from Drottninggatan, Stockholm and beyond.</p>
        <p className="text-sm text-text-secondary mb-12">
          Subscribe on <a href="https://www.youtube.com/@dave.r.stewart" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">YouTube</a> - 30K+ subscribers!
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {videos.map((video, index) => (
            <div key={index} data-testid={`video-${index}`} className="border border-border overflow-hidden">
              <div className="aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={video.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="p-4">
                <h3 className="font-heading font-semibold text-base">{video.title}</h3>
                <p className="text-sm text-text-secondary">{video.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== CD SHOP ==============
const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) { console.error("Error fetching products:", error); }
    finally { setLoading(false); }
  };

  const handleStripeCheckout = async (productId) => {
    setProcessingPayment(productId);
    try {
      const response = await axios.post(`${API}/checkout/stripe`, {
        product_id: productId, origin_url: window.location.origin, payment_method: "stripe"
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      alert("Error processing payment. Please try again.");
    } finally { setProcessingPayment(null); }
  };

  const handlePayPalCheckout = async (productId) => {
    try {
      const response = await axios.get(`${API}/paypal/product/${productId}`);
      const { title, price, currency, paypal_email } = response.data;
      const paypalUrl = `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent(paypal_email)}&item_name=${encodeURIComponent(title)}&amount=${price}&currency_code=${currency}`;
      window.open(paypalUrl, '_blank');
    } catch (error) {
      console.error("Error with PayPal:", error);
      alert("Error processing PayPal payment. Please try again.");
    }
  };

  const categories = [
    { id: "all", label: "All" },
    { id: "studio", label: "Studio Albums" },
    { id: "live", label: "Live" },
    { id: "dvd", label: "DVD" },
    { id: "custom", label: "Custom" }
  ];

  const filteredProducts = activeCategory === "all" ? products : products.filter(p => p.category === activeCategory);

  if (loading) return <div data-testid="shop-loading" className="pt-20 min-h-screen flex items-center justify-center"><div className="animate-pulse">Loading...</div></div>;

  return (
    <div data-testid="shop-page" className="pt-20">
      <div className="container-padding section-spacing">
        <h1 className="heading-1 mb-4">CD Shop</h1>
        <p className="body-text mb-4 max-w-2xl">Dave's complete collection. 185 SEK per CD/DVD including P&P and tax.</p>
        <p className="text-sm text-text-secondary mb-8">Custom CDs: 279 SEK - choose your own tracks + personal message!</p>
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((cat) => (
            <button key={cat.id} data-testid={`filter-${cat.id}`} onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 text-sm font-medium transition-all ${activeCategory === cat.id ? "bg-accent text-white" : "bg-bg-secondary text-text-secondary hover:bg-border"}`}>
              {cat.label} ({cat.id === "all" ? products.length : products.filter(p => p.category === cat.id).length})
            </button>
          ))}
        </div>
        <div className="space-y-6">
          {filteredProducts.map((product) => (
            <div key={product.id} data-testid={`product-${product.id}`} className="bg-white border border-border overflow-hidden">
              <div className="flex flex-col md:flex-row">
                {/* CD Cover */}
                <div className="w-full md:w-56 lg:w-64 flex-shrink-0 relative">
                  {product.category === "dvd" && <span className="absolute top-2 left-2 bg-accent text-white text-xs px-2 py-1 z-10 font-bold">DVD</span>}
                  {product.category === "live" && <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 z-10 font-bold">LIVE</span>}
                  {product.category === "custom" && <span className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 z-10 font-bold">CUSTOM</span>}
                  <div className="aspect-square overflow-hidden bg-bg-secondary max-h-64 md:max-h-none mx-auto md:mx-0" style={{maxWidth: '260px'}}>
                    <img src={product.image} alt={product.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = "https://img.youtube.com/vi/1iDTYnxCNXM/maxresdefault.jpg"; }} />
                  </div>
                </div>
                {/* Details & Tracks */}
                <div className="flex-1 p-5 md:p-6 flex flex-col">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                    <div>
                      <h3 className="font-heading font-bold text-lg md:text-xl">{product.title}</h3>
                      <p className="text-accent font-bold text-xl mt-1">{product.price} {product.currency}</p>
                    </div>
                  </div>
                  {/* Track Listing */}
                  <div className="flex-1 mb-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">Track Listing</p>
                    <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5">
                      {product.tracks.map((track, i) => (
                        <li key={i} className="text-sm text-text-secondary flex items-baseline gap-2 py-0.5">
                          <span className="text-xs text-accent font-mono w-5 flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
                          <span>{track}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                  {/* Buy Buttons */}
                  <div className="flex flex-wrap gap-3 mt-auto">
                    <button data-testid={`buy-stripe-${product.id}`} onClick={() => handleStripeCheckout(product.id)} disabled={processingPayment === product.id}
                      className="btn-primary text-xs py-3 px-6 disabled:opacity-50">
                      {processingPayment === product.id ? "Processing..." : "Buy with Stripe"}
                    </button>
                    <button data-testid={`buy-paypal-${product.id}`} onClick={() => handlePayPalCheckout(product.id)} className="btn-secondary text-xs py-3 px-6">Buy with PayPal</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== PAYMENT SUCCESS ==============
const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");

  const pollPaymentStatus = useCallback(async (attempt = 0) => {
    if (attempt >= 5) { setStatus("timeout"); return; }
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === "paid") { setStatus("success"); }
      else if (response.data.status === "expired") { setStatus("expired"); }
      else { setTimeout(() => pollPaymentStatus(attempt + 1), 2000); }
    } catch (error) { setStatus("error"); }
  }, [sessionId]);

  useEffect(() => { if (sessionId) pollPaymentStatus(); }, [sessionId, pollPaymentStatus]);

  return (
    <div data-testid="payment-success-page" className="pt-20 min-h-screen flex items-center justify-center">
      <div className="container-padding text-center">
        {status === "checking" && <div data-testid="payment-checking"><Clock className="w-16 h-16 mx-auto text-accent animate-pulse mb-4" /><h1 className="heading-2 mb-4">Processing Payment...</h1></div>}
        {status === "success" && <div data-testid="payment-success"><Check className="w-16 h-16 mx-auto text-green-600 mb-4" /><h1 className="heading-2 mb-4">Payment Successful!</h1><p className="body-text mb-8">Your CD will be shipped soon.</p><button onClick={() => navigate("/shop")} className="btn-primary">Continue Shopping</button></div>}
        {(status === "expired" || status === "timeout" || status === "error") && <div data-testid="payment-error"><h1 className="heading-2 mb-4">Payment Issue</h1><p className="body-text mb-8">Please check your email for confirmation or contact support.</p><button onClick={() => navigate("/shop")} className="btn-primary">Back to Shop</button></div>}
      </div>
    </div>
  );
};

// ============== SONGLIST ==============
const Songlist = () => {
  const [songlist, setSonglist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => { fetchSonglist(); }, []);

  const fetchSonglist = async () => {
    try {
      const response = await axios.get(`${API}/songlist`);
      setSonglist(response.data);
    } catch (error) { console.error("Error fetching songlist:", error); }
    finally { setLoading(false); }
  };

  const filteredSonglist = songlist.filter((item) => {
    const term = searchTerm.toLowerCase();
    return item.artist.toLowerCase().includes(term) || item.songs.some((song) => song.toLowerCase().includes(term));
  });

  const totalSongs = songlist.reduce((acc, item) => acc + item.songs.length, 0);

  return (
    <div data-testid="songlist-page" className="pt-20">
      <div className="container-padding section-spacing">
        <h1 className="heading-1 mb-4">Songlist</h1>
        <p className="body-text mb-2 max-w-2xl">Dave's extensive repertoire: <strong>{songlist.length} artists</strong>, <strong>{totalSongs}+ songs</strong>.</p>
        <p className="text-sm text-text-secondary mb-8">Classic rock, folk, pop, and many more. New songs added regularly!</p>
        <div className="mb-8 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
          <input type="text" data-testid="songlist-search" placeholder="Search artists or songs..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-border focus:border-accent focus:outline-none transition-colors" />
        </div>
        {loading ? <div className="animate-pulse">Loading songlist...</div> : (
          <>
            <p className="text-sm text-text-secondary mb-6">{filteredSonglist.length} artists found {searchTerm && `for "${searchTerm}"`}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSonglist.map((item, index) => (
                <div key={index} data-testid={`artist-${index}`} className="p-5 bg-white border border-border">
                  <h3 className="font-heading font-semibold text-base mb-2 text-accent">{item.artist}</h3>
                  <ul className="space-y-0.5">
                    {item.songs.map((song, songIndex) => (
                      <li key={songIndex} className="text-sm text-text-secondary">{song}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============== GIG GUIDE ==============
const GigGuide = () => (
  <div data-testid="gig-guide-page" className="pt-20">
    <div className="container-padding section-spacing">
      <h1 className="heading-1 mb-4">Gig Guide</h1>
      <p className="body-text mb-8 max-w-2xl">Check Dave's availability and upcoming performances.</p>
      <div className="bg-white border border-border overflow-hidden">
        <iframe data-testid="gig-calendar" src="https://www.google.com/calendar/embed?src=ptj94ik7edr1m5hoejolcej2ks%40group.calendar.google.com&ctz=Europe/Stockholm"
          className="w-full h-[500px] md:h-[700px]" frameBorder="0" scrolling="no" title="Dave Stewart Gig Calendar"></iframe>
      </div>
      <div className="mt-12 p-8 bg-bg-secondary border border-border">
        <h2 className="heading-2 mb-6">Booking & Pricing</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="heading-3 mb-4">Pricing Guidelines</h3>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li>1 gig: <strong>8,000 SEK</strong> + travel & accommodation</li>
              <li>2 gigs: <strong>14,000 SEK</strong> (7,000/gig) + travel</li>
              <li>3 gigs: <strong>18,000 SEK</strong> (6,000/gig) + travel</li>
              <li>4+ gigs: <strong>20,000 SEK</strong> (5,000/gig min) + travel</li>
              <li>7 consecutive days: <strong>28,000 SEK</strong> + travel</li>
              <li>14 consecutive days: <strong>35,000 SEK</strong> + travel</li>
            </ul>
          </div>
          <div>
            <h3 className="heading-3 mb-4">What's Included</h3>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li>Dave with guitar, microphone, and mixing board</li>
              <li>Venue provides PA system and mic stand</li>
              <li>Reasonable accommodation required</li>
              <li>Can recommend other artists at same rates</li>
              <li><strong>MOMS</strong> must be paid when booking other artists</li>
            </ul>
            <p className="mt-4 text-sm text-text-secondary"><strong>Cancellation:</strong> Extended bookings cancelled within 14 days liable for 50% payment.</p>
          </div>
        </div>
        <div className="mt-8"><Link to="/contact" className="btn-primary">Book Dave Now</Link></div>
      </div>
    </div>
  </div>
);

// ============== REVIEWS ==============
const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", location: "", rating: 5, review_text: "", event_type: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { fetchReviews(); }, []);

  const fetchReviews = async () => {
    try { const response = await axios.get(`${API}/reviews`); setReviews(response.data); }
    catch (error) { console.error("Error fetching reviews:", error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      setSubmitted(true);
      setFormData({ name: "", location: "", rating: 5, review_text: "", event_type: "" });
    } catch (error) { alert("Error submitting review."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="reviews-page" className="pt-20">
      <div className="container-padding section-spacing">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
          <div>
            <h1 className="heading-1 mb-4">Reviews</h1>
            <p className="body-text max-w-2xl">What customers say about Dave's performances.</p>
          </div>
          <button data-testid="write-review-btn" onClick={() => setShowForm(!showForm)} className="btn-primary">Write a Review</button>
        </div>
        {showForm && (
          <div data-testid="review-form-container" className="mb-12 p-8 bg-white border border-border">
            {submitted ? (
              <div data-testid="review-submitted" className="text-center py-8">
                <Check className="w-12 h-12 mx-auto text-green-600 mb-4" /><h3 className="heading-3 mb-2">Thank you!</h3>
                <p className="body-text">Your review has been submitted and will appear once approved.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h3 className="heading-3 mb-4">Share Your Experience</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><label className="block text-sm font-medium mb-2">Name *</label>
                    <input type="text" data-testid="review-name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium mb-2">Location</label>
                    <input type="text" data-testid="review-location" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" placeholder="City, Country" /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><label className="block text-sm font-medium mb-2">Event Type</label>
                    <input type="text" data-testid="review-event" value={formData.event_type} onChange={(e) => setFormData({ ...formData, event_type: e.target.value })} className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" placeholder="Wedding, Corporate, Pub..." /></div>
                  <div><label className="block text-sm font-medium mb-2">Rating *</label>
                    <div className="flex gap-2">{[1, 2, 3, 4, 5].map((star) => (
                      <button key={star} type="button" data-testid={`rating-${star}`} onClick={() => setFormData({ ...formData, rating: star })}
                        className={`p-2 ${formData.rating >= star ? "text-yellow-500" : "text-gray-300"}`}>
                        <Star className="w-6 h-6" fill={formData.rating >= star ? "currentColor" : "none"} /></button>))}</div></div>
                </div>
                <div><label className="block text-sm font-medium mb-2">Your Review *</label>
                  <textarea data-testid="review-text" required rows={4} value={formData.review_text} onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                    className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none resize-none" placeholder="Share your experience..." /></div>
                <button type="submit" data-testid="submit-review-btn" disabled={submitting} className="btn-primary disabled:opacity-50">{submitting ? "Submitting..." : "Submit Review"}</button>
              </form>
            )}
          </div>
        )}
        {loading ? <div className="animate-pulse">Loading reviews...</div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div key={index} data-testid={`review-${index}`} className="p-8 bg-white border border-border">
                <Quote className="w-8 h-8 text-accent mb-4" />
                <p className="body-text mb-6 italic">"{review.review_text}"</p>
                <div className="flex items-center gap-1 mb-4">{[...Array(review.rating)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-500" fill="currentColor" />)}</div>
                <p className="font-semibold text-text-primary">{review.name}</p>
                {review.location && <p className="text-sm text-text-secondary flex items-center gap-1"><MapPin className="w-3 h-3" /> {review.location}</p>}
                {review.event_type && <p className="text-sm text-accent mt-1">{review.event_type}</p>}
              </div>
            ))}
            {reviews.length === 0 && <p className="body-text col-span-3 text-center py-12">No reviews yet. Be the first to leave a review!</p>}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== CONTACT ==============
const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", event_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await axios.post(`${API}/contact`, formData); setSubmitted(true); }
    catch (error) { alert("Error sending message. Please try email directly."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      <div className="container-padding section-spacing">
        <h1 className="heading-1 mb-4">Book Dave Stewart</h1>
        <p className="body-text mb-12 max-w-2xl">Get in touch to book Dave for your event.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="p-8 bg-white border border-border mb-8">
              <h2 className="heading-2 mb-6">Contact Details</h2>
              <div className="space-y-4">
                <a href="mailto:dave@dave63.com" data-testid="contact-email" className="flex items-center gap-3 text-text-secondary hover:text-accent transition-colors"><Mail className="w-5 h-5" /> dave@dave63.com</a>
                <a href="tel:+46702256790" data-testid="contact-phone" className="flex items-center gap-3 text-text-secondary hover:text-accent transition-colors"><Phone className="w-5 h-5" /> +46 702 256 790</a>
              </div>
            </div>
            <div className="p-8 bg-bg-secondary border border-border">
              <h3 className="heading-3 mb-4">Quick Links</h3>
              <ul className="space-y-3">
                <li><Link to="/gig-guide" className="flex items-center gap-2 text-text-secondary hover:text-accent transition-colors"><Calendar className="w-4 h-4" /> Check Availability</Link></li>
                <li><a href="https://www.youtube.com/@dave.r.stewart" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-text-secondary hover:text-accent transition-colors"><Play className="w-4 h-4" /> YouTube Channel <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="http://www.facebook.com/dave.r.stewart" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-text-secondary hover:text-accent transition-colors"><ExternalLink className="w-4 h-4" /> Facebook <ExternalLink className="w-3 h-3" /></a></li>
              </ul>
            </div>
          </div>
          <div className="p-8 bg-white border border-border">
            {submitted ? (
              <div data-testid="contact-submitted" className="text-center py-12"><Check className="w-16 h-16 mx-auto text-green-600 mb-4" /><h2 className="heading-2 mb-4">Message Sent!</h2><p className="body-text">Dave will get back to you soon.</p></div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h2 className="heading-2 mb-4">Send a Message</h2>
                <div><label className="block text-sm font-medium mb-2">Name *</label><input type="text" data-testid="contact-name-input" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" /></div>
                <div><label className="block text-sm font-medium mb-2">Email *</label><input type="email" data-testid="contact-email-input" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" /></div>
                <div><label className="block text-sm font-medium mb-2">Phone</label><input type="tel" data-testid="contact-phone-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" /></div>
                <div><label className="block text-sm font-medium mb-2">Event Date</label><input type="date" data-testid="contact-date-input" value={formData.event_date} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" /></div>
                <div><label className="block text-sm font-medium mb-2">Message *</label><textarea data-testid="contact-message-input" required rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none resize-none" placeholder="Tell us about your event..." /></div>
                <button type="submit" data-testid="contact-submit-btn" disabled={submitting} className="btn-primary w-full disabled:opacity-50">{submitting ? "Sending..." : "Send Message"}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== PROMOTION ==============
const Promotion = () => {
  const materials = [
    { title: "Concert Poster", image: "https://www.dave63.com/little_poster.jpg", download: "https://www.dave63.com/poster.jpg" },
    { title: "Tartan Poster", image: "https://www.dave63.com/little_tartan.jpg", download: "https://www.dave63.com/tartan.jpg" },
  ];
  return (
    <div data-testid="promotion-page" className="pt-20">
      <div className="container-padding section-spacing">
        <h1 className="heading-1 mb-4">Promotional Material</h1>
        <p className="body-text mb-12 max-w-2xl">Download and print these posters to promote Dave's performance at your venue.</p>

        {/* Bio from Brophy Bookings */}
        <div className="mb-12 p-8 bg-bg-secondary border border-border max-w-4xl">
          <p className="overline mb-4">ABOUT DAVE</p>
          <h2 className="heading-2 mb-4">Scotland's Seasoned Performer</h2>
          <p className="body-text mb-4">
            Dave Stewart from Scotland, living in Sweden, has been playing for over 34 years in several countries from Switzerland to Scotland and England to Estonia. Been with Brophy Musos for 5-6 years, mostly playing Scandinavia and the Baltics with a few European gigs for fun too.
          </p>
          <p className="body-text mb-6">
            "I play rock and pop covers and love a good sing-along with audience participation - have a great time every time!"
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="https://brophybookings.com/genre/pop-rock/genre-solo/genre-act-pop-rock-dave-stewart" target="_blank" rel="noopener noreferrer" data-testid="brophy-link" className="btn-primary inline-flex items-center gap-2">
              <ExternalLink className="w-4 h-4" /> View on Brophy Bookings
            </a>
            <a href="https://brophybookings.com/booking-form?band_name=Dave+stewart&act=Solo" target="_blank" rel="noopener noreferrer" data-testid="brophy-book-link" className="btn-secondary inline-flex items-center gap-2">
              Book via Brophy
            </a>
          </div>
        </div>

        <h2 className="heading-2 mb-8">Downloadable Posters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
          {materials.map((item, index) => (
            <div key={index} data-testid={`promo-${index}`} className="bg-white border border-border overflow-hidden group">
              <div className="aspect-[3/4] overflow-hidden bg-bg-secondary">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6"><h3 className="heading-3 mb-4">{item.title}</h3>
                <a href={item.download} target="_blank" rel="noopener noreferrer" data-testid={`download-${index}`} className="btn-primary inline-flex items-center gap-2"><Download className="w-4 h-4" /> Download Full Size</a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== ADMIN DASHBOARD ==============
const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [activeTab, setActiveTab] = useState("reviews");
  const [reviews, setReviews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [songlist, setSonglist] = useState([]);
  const [newArtist, setNewArtist] = useState("");
  const [newSongs, setNewSongs] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      setToken(response.data.token);
      setIsLoggedIn(true);
      setLoginError("");
    } catch (error) { setLoginError("Invalid password"); }
  };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const [rev, notif, songs] = await Promise.all([
        axios.get(`${API}/admin/reviews?token=${token}`),
        axios.get(`${API}/admin/notifications?token=${token}`),
        axios.get(`${API}/songlist`)
      ]);
      setReviews(rev.data);
      setNotifications(notif.data);
      setSonglist(songs.data);
    } catch (error) { console.error("Error fetching admin data:", error); }
  }, [token]);

  useEffect(() => { if (isLoggedIn) fetchAll(); }, [isLoggedIn, fetchAll]);

  const toggleReview = async (id, approved) => {
    await axios.put(`${API}/admin/reviews/${id}?token=${token}&approved=${!approved}`);
    fetchAll();
  };

  const deleteReview = async (id) => {
    if (window.confirm("Delete this review?")) {
      await axios.delete(`${API}/admin/reviews/${id}?token=${token}`);
      fetchAll();
    }
  };

  const markRead = async (id) => {
    await axios.put(`${API}/admin/notifications/${id}/read?token=${token}`);
    fetchAll();
  };

  const addSonglistEntry = async (e) => {
    e.preventDefault();
    if (!newArtist || !newSongs) return;
    const songs = newSongs.split(",").map(s => s.trim()).filter(Boolean);
    await axios.post(`${API}/admin/songlist?token=${token}`, { artist: newArtist, songs });
    setNewArtist("");
    setNewSongs("");
    fetchAll();
  };

  const deleteSonglistEntry = async (id) => {
    if (window.confirm("Remove this artist?")) {
      await axios.delete(`${API}/admin/songlist/${id}?token=${token}`);
      fetchAll();
    }
  };

  if (!isLoggedIn) {
    return (
      <div data-testid="admin-login-page" className="pt-20 min-h-screen flex items-center justify-center">
        <div className="p-8 bg-white border border-border w-full max-w-md">
          <div className="flex items-center gap-3 mb-8"><Shield className="w-8 h-8 text-accent" /><h1 className="heading-2">Admin Dashboard</h1></div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div><label className="block text-sm font-medium mb-2">Password</label>
              <input type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" /></div>
            {loginError && <p className="text-red-600 text-sm">{loginError}</p>}
            <button type="submit" data-testid="admin-login-btn" className="btn-primary w-full">Login</button>
          </form>
        </div>
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div data-testid="admin-dashboard" className="pt-20">
      <div className="container-padding section-spacing">
        <div className="flex items-center gap-3 mb-8"><Shield className="w-8 h-8 text-accent" /><h1 className="heading-1">Admin Dashboard</h1></div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-border pb-4">
          <button onClick={() => setActiveTab("reviews")} className={`px-4 py-2 text-sm font-medium ${activeTab === "reviews" ? "bg-accent text-white" : "bg-bg-secondary"}`}>
            Reviews ({reviews.length})
          </button>
          <button onClick={() => setActiveTab("notifications")} className={`px-4 py-2 text-sm font-medium relative ${activeTab === "notifications" ? "bg-accent text-white" : "bg-bg-secondary"}`}>
            Booking Notifications {unreadCount > 0 && <span className="ml-1 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">{unreadCount}</span>}
          </button>
          <button onClick={() => setActiveTab("songlist")} className={`px-4 py-2 text-sm font-medium ${activeTab === "songlist" ? "bg-accent text-white" : "bg-bg-secondary"}`}>
            Manage Songlist ({songlist.length})
          </button>
        </div>

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <div data-testid="admin-reviews">
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className={`p-6 border ${review.approved ? "border-green-200 bg-green-50" : "border-yellow-200 bg-yellow-50"}`}>
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 ${review.approved ? "bg-green-200 text-green-800" : "bg-yellow-200 text-yellow-800"}`}>
                          {review.approved ? "APPROVED" : "PENDING"}
                        </span>
                        <div className="flex">{[...Array(review.rating)].map((_, i) => <Star key={i} className="w-3 h-3 text-yellow-500" fill="currentColor" />)}</div>
                      </div>
                      <p className="text-sm italic mb-2">"{review.review_text}"</p>
                      <p className="text-sm"><strong>{review.name}</strong>{review.location && ` - ${review.location}`}{review.event_type && ` (${review.event_type})`}</p>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`toggle-review-${review.id}`} onClick={() => toggleReview(review.id, review.approved)}
                        className={`p-2 ${review.approved ? "text-yellow-600 hover:bg-yellow-100" : "text-green-600 hover:bg-green-100"}`} title={review.approved ? "Unapprove" : "Approve"}>
                        {review.approved ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                      </button>
                      <button data-testid={`delete-review-${review.id}`} onClick={() => deleteReview(review.id)} className="p-2 text-red-600 hover:bg-red-100" title="Delete">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {reviews.length === 0 && <p className="body-text text-center py-8">No reviews yet.</p>}
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div data-testid="admin-notifications">
            <div className="space-y-4">
              {notifications.map((notif) => (
                <div key={notif.id} className={`p-6 border ${notif.read ? "border-border bg-white" : "border-accent bg-blue-50"}`}>
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="flex-1">
                      {!notif.read && <span className="text-xs bg-accent text-white px-2 py-1 mb-2 inline-block">NEW</span>}
                      <p className="font-semibold">{notif.name}</p>
                      <p className="text-sm text-text-secondary">{notif.email} {notif.phone && `| ${notif.phone}`}</p>
                      {notif.event_date && <p className="text-sm text-accent">Event date: {notif.event_date}</p>}
                      <p className="text-sm mt-2">{notif.message}</p>
                      <p className="text-xs text-text-secondary mt-2">{new Date(notif.created_at).toLocaleDateString()}</p>
                    </div>
                    {!notif.read && (
                      <button onClick={() => markRead(notif.id)} className="p-2 text-accent hover:bg-blue-100" title="Mark as read">
                        <Check className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {notifications.length === 0 && <p className="body-text text-center py-8">No booking requests yet.</p>}
            </div>
          </div>
        )}

        {/* Songlist Management Tab */}
        {activeTab === "songlist" && (
          <div data-testid="admin-songlist">
            <form onSubmit={addSonglistEntry} className="p-6 bg-bg-secondary border border-border mb-8">
              <h3 className="heading-3 mb-4 flex items-center gap-2"><Plus className="w-5 h-5" /> Add / Update Artist</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-1">Artist Name</label>
                  <input type="text" data-testid="admin-artist-name" value={newArtist} onChange={(e) => setNewArtist(e.target.value)} placeholder="e.g. Adele"
                    className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" /></div>
                <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Songs (comma separated)</label>
                  <input type="text" data-testid="admin-songs" value={newSongs} onChange={(e) => setNewSongs(e.target.value)} placeholder="e.g. Rolling in the Deep, Someone Like You"
                    className="w-full px-4 py-3 border border-border focus:border-accent focus:outline-none" /></div>
              </div>
              <p className="text-xs text-text-secondary mt-2 mb-4">If artist already exists, songs will be merged.</p>
              <button type="submit" data-testid="admin-add-song-btn" className="btn-primary text-sm">Add / Update</button>
            </form>
            <div className="space-y-3">
              {songlist.map((item) => (
                <div key={item.id || item.artist} className="p-4 bg-white border border-border flex flex-wrap justify-between items-start gap-2">
                  <div className="flex-1">
                    <h4 className="font-heading font-semibold text-sm text-accent">{item.artist}</h4>
                    <p className="text-xs text-text-secondary">{item.songs.join(" • ")}</p>
                  </div>
                  {item.id && <button onClick={() => deleteSonglistEntry(item.id)} className="p-1 text-red-500 hover:bg-red-50" title="Remove"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== FOOTER ==============
const Footer = () => (
  <footer data-testid="footer" className="bg-text-primary text-white py-12">
    <div className="container-padding">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div>
          <h3 className="font-heading font-bold text-xl mb-4">Dave Stewart</h3>
          <p className="text-gray-400 text-sm">Professional musician & entertainer based in Sweden.</p>
          <p className="text-gray-400 text-sm mt-2">30K+ YouTube subscribers</p>
        </div>
        <div>
          <h4 className="font-bold mb-4">Quick Links</h4>
          <div className="space-y-2">
            <Link to="/shop" className="block text-gray-400 hover:text-white text-sm transition-colors">CD Shop</Link>
            <Link to="/gig-guide" className="block text-gray-400 hover:text-white text-sm transition-colors">Gig Guide</Link>
            <Link to="/contact" className="block text-gray-400 hover:text-white text-sm transition-colors">Book Dave</Link>
            <Link to="/reviews" className="block text-gray-400 hover:text-white text-sm transition-colors">Reviews</Link>
          </div>
        </div>
        <div>
          <h4 className="font-bold mb-4">Connect</h4>
          <div className="flex gap-4 mb-4">
            <a href="https://www.youtube.com/@dave.r.stewart" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Play size={20} /></a>
            <a href="http://twitter.com/justdave63" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><ExternalLink size={20} /></a>
            <a href="http://www.facebook.com/dave.r.stewart" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><ExternalLink size={20} /></a>
          </div>
          <p className="text-gray-400 text-sm">dave@dave63.com</p>
          <p className="text-gray-400 text-sm">+46 702 256 790</p>
        </div>
      </div>
      <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
        <p>&copy; {new Date().getFullYear()} Dave Stewart. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// ============== MAIN APP ==============
function App() {
  return (
    <div className="App min-h-screen bg-bg-primary">
      <BrowserRouter>
        <Navigation />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/videos" element={<Videos />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/songlist" element={<Songlist />} />
            <Route path="/gig-guide" element={<GigGuide />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/promotion" element={<Promotion />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
